import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, RefreshCw } from "lucide-react";
import api from "../../lib/api";

/**
 * Environment Health widget for the Admin Dashboard. Consumes
 * `GET /api/admin/config/check` (admin-only) and renders a compact
 * status card per integration: DB, Stripe, Email delivery (SMTP-first,
 * Resend fallback), JWT, CORS.
 *
 * Never displays secrets — the backend endpoint returns only booleans
 * and short human-readable status labels.
 */
const STATUS_META = {
  ok:       { Icon: CheckCircle2,  color: "text-emerald-600 dark:text-emerald-400", label: "OK" },
  warning:  { Icon: AlertTriangle, color: "text-amber-600 dark:text-amber-400",     label: "Warning" },
  error:    { Icon: XCircle,       color: "text-red-600 dark:text-red-400",         label: "Error" },
};

const OVERALL_META = {
  healthy:  { dot: "bg-emerald-500", label: "All systems go" },
  warning:  { dot: "bg-amber-500",   label: "Attention needed" },
  critical: { dot: "bg-red-500",     label: "Action required" },
};

export default function EnvironmentHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchHealth = () => {
    setLoading(true);
    setError("");
    api.get("/admin/config/check")
      .then((r) => {
        const d = r?.data;
        if (d && typeof d === "object" && Array.isArray(d.checks)) setData(d);
        else setData(null);
      })
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load environment health"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchHealth, []);

  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded"
      data-testid="env-health-panel"
    >
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} className="text-neutral-500 dark:text-neutral-400" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              Environment
            </p>
            <h2 className="font-display text-2xl mt-1">Health check</h2>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <div
              className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400"
              data-testid="env-health-overall"
            >
              <span
                className={`w-2 h-2 rounded-full ${OVERALL_META[data.overall]?.dot || "bg-neutral-400"}`}
              />
              {OVERALL_META[data.overall]?.label || "Unknown"}
            </div>
          )}
          <button
            type="button"
            onClick={fetchHealth}
            disabled={loading}
            data-testid="env-health-refresh"
            className="text-[11px] tracking-widest uppercase border-b border-black/30 dark:border-white/30 hover:border-black dark:hover:border-white pb-0.5 flex items-center gap-1 disabled:opacity-40"
            aria-label="Refresh environment health"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !data && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400" data-testid="env-health-loading">
          Checking environment…
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" data-testid="env-health-error">
          {error}
        </p>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.checks.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.warning;
            const Icon = meta.Icon;
            return (
              <div
                key={c.id}
                className="border border-black/10 dark:border-white/10 p-4 rounded flex gap-3"
                data-testid={`env-health-check-${c.id}`}
              >
                <Icon size={18} className={`shrink-0 mt-0.5 ${meta.color}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{c.label}</p>
                    <span
                      className={`text-[10px] tracking-widest uppercase ${meta.color}`}
                      data-testid={`env-health-status-${c.id}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 break-words">
                    {c.detail}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.metrics && (
        <div className="mt-6 pt-4 border-t border-black/5 dark:border-white/5 flex flex-wrap gap-6 text-xs text-neutral-500 dark:text-neutral-400">
          <span data-testid="env-health-metric-bookings">
            Bookings in DB: <strong className="text-neutral-900 dark:text-neutral-100">{data.metrics.bookings}</strong>
          </span>
          <span data-testid="env-health-metric-vehicles">
            Vehicles in DB: <strong className="text-neutral-900 dark:text-neutral-100">{data.metrics.vehicles}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
